import { HttpClient } from '@angular/common/http';
import { AtsLoggerService } from '@ats/logger';
import { BehaviorSubject, combineLatest, iif, Observable, of } from 'rxjs';
import { distinctUntilChanged, map, mapTo, switchMap, take } from 'rxjs/operators';
import { CmAuthService, CmPublicTokenData } from '../services/auth.service';
import { CmFeatureService } from '../services/features.service';
import { CmSettingsService } from '../services/settings.service';
import { AppUser } from './app-user.interface';
import { ForgotPasswordRequest } from './forgot-password-request.interface';
import { CmInterceptHeaders } from './intercept-headers';
import { ResetPasswordRequest } from './password-reset-request.interface';

enum UserActionType {
	UPDATE_USER = 'Update the current app user',
}

interface UserAction {
	type: UserActionType;
	payload: AppUser | null;
}

class UserState {
	user: AppUser | null = null;
}

const reduce = (state: UserState, action: UserAction) => {
	switch (action.type) {
		case UserActionType.UPDATE_USER:
			return { ...state, user: action.payload ? { ...(action.payload as AppUser) } : null };
	}
};

export abstract class CmBaseUserService {
	readonly user$: Observable<AppUser | null>;
	readonly authenticated$: Observable<boolean>;
	readonly canLocalize$: Observable<boolean>;
	readonly impersonating$: Observable<boolean>;

	protected state = new UserState();
	protected queryUser$: (features: string[], token: CmPublicTokenData) => Observable<AppUser | null>;
	protected readonly dispatch = new BehaviorSubject<UserState>(this.state);

	private readonly tokenAndFeatures$: Observable<{ token: CmPublicTokenData; features: string[] }>;

	constructor(
		protected auth: CmAuthService,
		protected settings: CmSettingsService,
		protected features: CmFeatureService,
		protected logger: AtsLoggerService,
		protected http: HttpClient
	) {
		this.user$ = this.dispatch.asObservable().pipe(
			map(state => state.user),
			distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
		);
		this.authenticated$ = this.user$.pipe(map(user => !!user));
		this.canLocalize$ = this.user$.pipe(map(user => (user || {}).localizationEnabled || false));
		this.impersonating$ = this.user$.pipe(map(user => (user || {}).isImpersonating || false));
		this.user$.subscribe(user => this.auth.updateGuardUser(user));
		this.tokenAndFeatures$ = combineLatest([this.auth.token$, this.features.protected$]).pipe(
			map(([token, features]) => ({ token, features }))
		);
	}

	/**
	 * Updates the user currently in state
	 * @private
	 * @method
	 * @param {AppUser | null} user - The user to add to state, or null to clear it out
	 * @return {void}
	 */
	protected updateUser(user: AppUser | null): void {
		const type = UserActionType.UPDATE_USER;
		const action = { payload: user, type };
		this.dispatch.next((this.state = reduce(this.state, action)));
	}

	/**
	 * Triggers a forgotten password email
	 * @method
	 * @private
	 * @param {string} [loginId] - The username for the password reset
	 * @returns {Observable<boolean>} - Returns an observable of a boolean set to true
	 */
	protected forgotPassword(request: ForgotPasswordRequest, portal: 'candidate' | 'sponsor'): Observable<boolean> {
		return this.settings.apiWithContext$.pipe(
			take(1),
			switchMap(apiWithContext =>
				this.http.post(`${apiWithContext}/${portal}/reset-password`, request, {
					headers: { ...CmInterceptHeaders.BypassAuth, ...CmInterceptHeaders.SuppressError },
				})
			),
			mapTo(true)
		);
	}

	/**
	 * Resets the password for a signed-in app user
	 * @method
	 * @protected
	 * @param {Object} obj - An object
	 * @param {string} obj.token - Token from the query params on the reset password page
	 * @param {string} obj.newPassword - The new password
	 * @returns {Observable<boolean>} - Returns an observable of a boolean set to true
	 */
	protected resetPassword(
		{ token, newPassword }: ResetPasswordRequest,
		portal: 'candidate' | 'sponsor'
	): Observable<boolean> {
		return this.settings.apiWithContext$.pipe(
			take(1),
			switchMap(apiWithContext =>
				this.http.post(
					`${apiWithContext}/${portal}/change-password`,
					{ token, newPassword },
					{ headers: { ...CmInterceptHeaders.BypassAuth, ...CmInterceptHeaders.IgnoreAuthError } }
				)
			),
			mapTo(true)
		);
	}

	/**
	 * Load the current logged in web user/candidate from GraphQL
	 * @method
	 * @protected
	 * @return {Observable<AppUser>} - Returns an observable containing the AppUser
	 */
	protected monitorUserChanges$(): void {
		if (!this.queryUser$) {
			this.logger.critical(
				'User Service was not setup properly. A queryUser$ method is required and is undefined.'
			);
		} else {
			this.tokenAndFeatures$
				.pipe(
					switchMap(({ token, features }) =>
						iif(() => !token || !features, of(null), this.queryUser$(features, token))
					)
				)
				.subscribe(user => this.updateUser(user));
		}
	}
}
